import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { motion } from 'framer-motion';

interface Client {
  email: string;
  name: string;
  phone: string;
  total_requests: number;
  total_spent: number;
  last_partner: string;
  last_request: string;
}

export default function AdminAllClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    // Agrupamos clientes con una lógica más robusta para evitar colisiones
    const { data } = await supabase
      .from('alterations')
      .select('client_name, client_email, client_phone, price, created_at, partners(name)')
      .order('created_at', { ascending: false });
    
    if (data) {
      const grouped = data.reduce((acc: any, curr) => {
        // Generamos una llave única basada en email, teléfono o nombre como último recurso
        // Si no hay email ni teléfono, usamos el nombre + un sufijo para no mezclar si no estamos seguros
        const key = curr.client_email || curr.client_phone || `name_${curr.client_name}`;
        
        if (!acc[key]) {
          acc[key] = {
            email: curr.client_email || 'Sin email',
            name: curr.client_name,
            phone: curr.client_phone || 'Sin teléfono',
            total_requests: 0,
            total_spent: 0,
            last_partner: curr.partners?.name,
            last_request: curr.created_at
          };
        }
        acc[key].total_requests += 1;
        acc[key].total_spent += (curr.price || 0);
        return acc;
      }, {});

      setClients(Object.values(grouped));
    }
    setLoading(false);
  };

  const filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(search.toLowerCase()) || 
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-brand-black text-2xl">Directorio de Clientes B2B</h2>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">Base de datos unificada de clientes que han utilizado servicios de tus partners.</p>
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-brand-gray-400" />
          <input 
            type="text" 
            placeholder="Buscar por nombre, email o tel..." 
            className="pl-11 pr-6 py-2.5 bg-white border border-brand-gray-200 rounded-full text-sm font-sans focus:outline-none focus:border-brand-black w-72 shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-brand-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-brand-gray-50 border-b border-brand-gray-100">
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Cliente</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400">Contacto</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400 text-center">Nº Solicitudes</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400 text-center">Gasto Total</th>
              <th className="px-6 py-4 font-sans text-[11px] uppercase tracking-wider text-brand-gray-400 text-right">Última Visita</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-gray-100">
            {filteredClients.map((client, idx) => (
              <tr key={idx} className="hover:bg-brand-gray-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-sage/10 flex items-center justify-center text-brand-sage font-serif font-bold text-lg">
                      {client.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-sans text-brand-black font-bold text-sm tracking-tight">{client.name}</p>
                      <p className="font-sans text-[10px] text-brand-gray-400 uppercase tracking-widest mt-0.5">Vía {client.last_partner}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <p className="font-sans text-xs text-brand-gray-600 font-medium">{client.email}</p>
                    <p className="font-sans text-[10px] text-brand-gray-400 uppercase tracking-wider">{client.phone}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className="bg-brand-black text-white px-3 py-1 rounded-full text-[10px] font-bold font-sans tracking-widest">
                    {client.total_requests}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <p className="font-serif text-base font-bold text-brand-black">{client.total_spent.toFixed(2)}€</p>
                </td>
                <td className="px-6 py-4 text-right">
                  <p className="font-sans text-[10px] text-brand-gray-400 uppercase tracking-widest font-bold">
                    {new Date(client.last_request).toLocaleDateString()}
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredClients.length === 0 && !loading && (
          <div className="py-24 text-center font-sans text-brand-gray-400 italic">No se han encontrado registros en el directorio.</div>
        )}
      </div>
    </div>
  );
}
